<template>
  <div class="land-page">
    <v-container fluid class="land-container">
      <div class="breadcrumb-line">{{ $t("reports.reportsBreadcrumb") }} <span>›</span> {{ $t("reports.landSuitability") }}</div>

      <div class="page-heading">
        <div class="title-wrap">
          <h1>{{ $t("reports.landSuitability") }}</h1>
          <!-- <button class="guide-button" type="button">
            <v-icon size="15" color="white">mdi-help-circle-outline</v-icon>
            {{ $t("reports.aiReportGuide") }}
          </button> -->
        </div>
        <div class="heading-actions">
          <v-btn outlined color="primary" class="outline-action" @click="$router.push({ name: 'aiReportsDashboard' })">
            {{ $t('reports.backToDashboard') }}
          </v-btn>
          <v-btn
            color="primary"
            class="primary-action"
            :to="{ name: 'landSuitabilityDesignCreate' }"
          >
            {{ $t('reports.createNewReport') }}
          </v-btn>
        </div>
      </div>

      <section class="reports-card">
        <h2>{{ $t("reports.listOfReports") }}</h2>
        <div class="table-toolbar">
          <v-text-field
            v-model="search"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('search')"
            class="report-search"
            @keyup.enter="applySearch"
          />
          <div class="pagination-tools">
            <span>{{ rangeLabel }}</span>
            <button
              type="button"
              :disabled="tableOptions.page <= 1"
              @click="pageChange(false)"
            >
              <v-icon size="20">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              :disabled="tableOptions.page >= totalPages"
              @click="pageChange(true)"
            >
              <v-icon size="20">mdi-chevron-right</v-icon>
            </button>
            <v-menu
              v-model="settingsMenu"
              :close-on-content-click="false"
              left
              offset-y
              min-width="410"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  :aria-label="$t('reports.customizeTable')"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="18">mdi-cog-outline</v-icon>
                </button>
              </template>
              <v-card class="customization-card">
                <v-card-title>{{ $t("reports.customization") }}</v-card-title>
                <v-divider />
                <v-card-text>
                  <div class="customization-label">{{ $t("reports.recordsPerPage") }}</div>
                  <v-slider
                    v-model="itemsPerPageSlider"
                    :tick-labels="itemsPerPageOptions"
                    :max="itemsPerPageOptions.length - 1"
                    step="1"
                    ticks="always"
                    tick-size="4"
                    color="primary"
                    track-color="grey lighten-2"
                  />
                  <v-divider class="customization-divider" />
                  <div class="customization-label">{{ $t("reports.visibleColumns") }}</div>
                  <div class="column-options">
                    <v-checkbox
                      v-for="header in customizableHeaders"
                      :key="header.value"
                      v-model="selectedHeaders"
                      :label="header.text"
                      :value="header.value"
                      color="primary"
                      dense
                      hide-details
                    />
                  </div>
                </v-card-text>
                <v-card-actions class="customization-actions">
                  <v-btn outlined color="primary" @click="resetTableSettings">
                    {{ $t('reports.restoreDefault') }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    :disabled="!selectedHeaders.length"
                    @click="applyTableSettings"
                  >
                    {{ $t("reports.apply") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
        </div>

        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :loading="loading"
          :server-items-length="totalItems"
          :items-per-page="tableOptions.itemsPerPage"
          hide-default-footer
          class="reports-table"
        >
          <template v-slot:item.id="{ item }">
            <button class="report-link" type="button" @click="openReport(item)">
              AD{{ String(item.id).padStart(6, "0") }}
            </button>
          </template>
          <template v-slot:item.farm="{ item }">
            <div class="farm-cell">
              <strong>{{ item.userFarm ? item.userFarm.farmName : "—" }}</strong>
              <span>FR{{ String(item.userFarm ? item.userFarm.id : item.farm_id || "").padStart(6, "0") }}</span>
            </div>
          </template>
          <template v-slot:item.producer="{ item }">
            {{ producerName(item) }}
          </template>
          <template v-slot:item.issuedDate="{ item }">
            {{ formatReportDate(item.issuedDate) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon size="20">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openReport(item)">
                  <v-list-item-title>{{ $t('reports.viewReport') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadReport(item)">
                  <v-list-item-title>{{ $t("reports.download") }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item class="delete-menu-item" @click="confirmDelete(item)">
                  <v-list-item-title>{{ $t("delete") }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-slot:no-data>
            <div class="empty-state">{{ $t('reports.noLandSuitabilityReportsFound') }}</div>
          </template>
        </v-data-table>
      </section>
    </v-container>
    <confirm-box
      :dialog="deleteDialog"
      :title="$t('reports.deleteReport')"
      :message="$t('reports.confirmDeleteLandSuitabilityReport')"
      :confirm-btn-text="$t('delete')"
      :cancel-btn-text="$t('cancel')"
      :cancel-btn-outlined="true"
      @cancel:action="closeDeleteDialog"
      @continue:action="deleteReport"
    />
  </div>
</template>

<script>
import moment from "moment";
import ConfirmBox from "@/components/ConfirmBox.vue";
import LandSuitabilityAndWeatherService from "@/_services/LandSuitabilityAndWeatherService";

export default {
  name: "LandSuitabilityLists",
  components: {
    ConfirmBox,
  },
  data() {
    return {
      search: "",
      items: [],
      loading: false,
      totalItems: 0,
      totalPages: 1,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      settingsMenu: false,
      itemsPerPageSlider: 1,
      itemsPerPageOptions: [5, 10, 25, 50],
      selectedHeaders: [],
      tableHeaders: [],
      deleteDialog: false,
      deleting: false,
      selectedReport: null,
      headers: [
        { text: this.$t("reports.reportId"), value: "id", sortable: true },
        { text: this.$t("reports.farmAndFarmId"), value: "farm", sortable: false },
        { text: this.$t("reports.producerName"), value: "producer", sortable: false },
        { text: this.$t("reports.dateOfReport"), value: "issuedDate", sortable: true },
        { text: "", value: "actions", sortable: false, align: "end", width: 54 },
      ],
    };
  },
  computed: {
    customizableHeaders() {
      return this.headers.filter((header) => header.text);
    },
    rangeLabel() {
      if (!this.totalItems) return "0 of 0";
      const to = Math.min(
        this.tableOptions.page * this.tableOptions.itemsPerPage,
        this.totalItems
      );
      return `${to} of ${this.totalItems}`;
    },
  },
  mounted() {
    this.selectedHeaders = this.customizableHeaders.map((header) => header.value);
    this.tableHeaders = [...this.headers];
    this.getLandSuitability();
  },
  methods: {
    producerName(item) {
      if (!item.user) return "—";
      return [item.user.firstName, item.user.lastName].filter(Boolean).join(" ") || "—";
    },
    formatReportDate(date) {
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
    applySearch() {
      this.tableOptions.page = 1;
      this.getLandSuitability();
    },
    async getLandSuitability() {
      this.loading = true;
      try {
        const response = await LandSuitabilityAndWeatherService.getLandSuitabilityLists({
          limit: this.tableOptions.itemsPerPage,
          search: this.search,
          page: this.tableOptions.page,
        });
        this.items = response.data.rows || [];
        this.totalItems = response.data.count || 0;
        this.totalPages = Math.max(
          1,
          Math.ceil(this.totalItems / this.tableOptions.itemsPerPage)
        );
      } catch (error) {
        this.items = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },
    pageChange(next) {
      this.tableOptions.page += next ? 1 : -1;
      this.getLandSuitability();
    },
    resetTableSettings() {
      this.selectedHeaders = this.customizableHeaders.map((header) => header.value);
      this.itemsPerPageSlider = 1;
      this.applyTableSettings();
    },
    applyTableSettings() {
      this.tableOptions.itemsPerPage =
        this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.tableOptions.page = 1;
      this.tableHeaders = this.headers.filter(
        (header) =>
          !header.text || this.selectedHeaders.includes(header.value)
      );
      this.settingsMenu = false;
      this.getLandSuitability();
    },
    openReport(item) {
      this.$router.push({
        name: "landSuitabilityDesignView",
        params: { id: item.id },
      });
    },
    downloadReport(item) {
      this.$router.push({
        name: "landSuitabilityDesignView",
        params: { id: item.id },
        query: { download: "1" },
      });
    },
    confirmDelete(item) {
      this.selectedReport = item;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.selectedReport = null;
    },
    async deleteReport() {
      if (!this.selectedReport || this.deleting) return;
      this.deleteDialog = false;
      this.deleting = true;
      try {
        const response =
          await LandSuitabilityAndWeatherService.deleteLandSuitabilityReport(
            this.selectedReport.id
          );
        if (!response || response.success === false) {
          throw new Error(
            (response && (response.message || response.msg)) ||
              this.$t("reports.unableToDeleteReport")
          );
        }
        if (this.items.length === 1 && this.tableOptions.page > 1) {
          this.tableOptions.page -= 1;
        }
        this.$notify({
          title: this.$t("reports.landSuitabilityReportDeletedSuccessfully"),
          type: "success",
        });
        await this.getLandSuitability();
      } catch (error) {
        this.$notify({
          title:
            (error && error.message) ||
            this.$t("reports.unableToDeleteReportRetry"),
          type: "error",
        });
      } finally {
        this.deleting = false;
        this.selectedReport = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.land-page {
  min-height: 100%;
  background: #f5f7fa;
  color: #20262d;
}

.land-container {
  padding: 28px 32px 42px;
}

.breadcrumb-line {
  margin-bottom: 12px;
  color: #7c858d;
  font-size: 12px;

  span {
    margin: 0 4px;
  }
}

.page-heading,
.title-wrap,
.heading-actions,
.table-toolbar,
.pagination-tools {
  display: flex;
  align-items: center;
}

.page-heading {
  justify-content: space-between;
  margin-bottom: 22px;
}

.title-wrap {
  gap: 14px;

  h1 {
    margin: 0;
    font-size: 27px;
    line-height: 1.2;
    font-weight: 700;
  }
}

.guide-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding: 0 13px;
  border-radius: 16px;
  background: #18b879;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.heading-actions {
  gap: 14px;

  .v-btn {
    min-width: 170px;
    height: 42px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 12px;
    font-weight: 700;
  }
}

.reports-card {
  padding: 20px 24px 28px;
  background: #fff;

  h2 {
    margin: 0 0 20px;
    font-size: 18px;
    font-weight: 600;
  }
}

.table-toolbar {
  justify-content: space-between;
  margin-bottom: 16px;
}

.report-search {
  max-width: 290px;

  ::v-deep .v-input__slot {
    min-height: 42px;
    border-radius: 2px;
  }
}

.pagination-tools {
  gap: 10px;
  color: #68727b;
  font-size: 16px;

  button {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border: 1px solid #8ed9bf;
    border-radius: 50%;
    background: #fff;
  }

  button:disabled {
    opacity: 0.45;
  }
}

.customization-card {
  .v-card__title {
    padding: 16px 20px;
    color: #232b30;
    font-size: 16px;
    font-weight: 600;
  }

  .v-card__text {
    padding: 16px 20px 8px;
  }
}

.customization-label {
  margin-bottom: 12px;
  color: #30393f;
  font-size: 12px;
  font-weight: 600;
}

.customization-divider {
  margin: 8px 0 16px;
}

.column-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 18px;

  ::v-deep .v-label {
    color: #4c565d;
    font-size: 12px;
  }
}

.customization-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px 20px 20px;

  .v-btn {
    width: 100%;
    height: 38px;
    margin: 0 !important;
    border-radius: 3px;
    box-shadow: none;
    font-size: 11px;
    font-weight: 700;
  }
}

.reports-table {
  ::v-deep thead tr {
    background: #16824f;
  }

  ::v-deep thead th {
    height: 46px;
    border-bottom: 0 !important;
    color: #fff !important;
    font-size: 12px;
    font-weight: 600;
  }

  ::v-deep tbody tr:nth-child(even) {
    background: #f7f7f7;
  }

  ::v-deep tbody td {
    height: 48px;
    border-bottom: 0 !important;
    color: #454c52;
    font-size: 12px;
  }
}

.report-link {
  color: #30383f;
}

.delete-menu-item {
  ::v-deep .v-list-item__title {
    color: #e53935;
  }
}

.farm-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.35;

  strong {
    font-weight: 500;
  }

  span {
    color: #727b83;
    font-size: 10px;
  }
}

.empty-state {
  padding: 36px 0;
  color: #7c858d;
}

@media (max-width: 900px) {
  .page-heading {
    align-items: flex-start;
    gap: 18px;
    flex-direction: column;
  }

  .table-toolbar {
    align-items: stretch;
    gap: 14px;
    flex-direction: column;
  }

  .report-search {
    max-width: 100%;
  }

  .pagination-tools {
    justify-content: flex-end;
  }
}
</style>
